import { useEffect, useRef, useState } from "react";
import api, { DocumentTranslation } from "../../../../hooks/api-new";
import { Setting } from "./Settings";
import classNames from "./DocumentTranslation.module.css";
import { useAxios } from "../../../../hooks/axios";
import Loader from "../../../common/Loader";
import Button from "../../../common/Button";
import ConfirmActionDialog from "../../../common/dialogs/ConfirmActionDialog";
import { getLanguageFlag, getLanguageName, supportedLanguages } from "../../../../types";
import { IoClose, IoCloudDownloadOutline, IoDocumentTextOutline, IoChevronDown } from "react-icons/io5";
import { toast } from "react-toastify";
import axios from "axios";

// Separate instance for direct S3 uploads — no auth headers, but stub adapter
// is attached in VITE_STUB mode so uploads are intercepted during development.
const s3Axios = import.meta.env.VITE_STUB ? null : axios.create();
import LanguageSelect from "../../../common/LanguageSelect";

const ALL_LANGUAGES = Object.values(supportedLanguages)
  .flatMap((langs) => Array.from(langs))
  .filter((value, index, self) => self.indexOf(value) === index)
  .sort((a, b) => (getLanguageName(a) ?? a).localeCompare(getLanguageName(b) ?? b));

function StatusBadge({ status }: { status: DocumentTranslation["status"] }) {
  return (
    <span className={`${classNames.StatusBadge} ${classNames[`Status_${status}`]}`}>
      {status === "processing" && "Processing..."}
      {status === "completed" && "Completed"}
      {status === "failed" && "Failed"}
    </span>
  );
}

const PROGRESS_CHECKPOINTS = [30, 60, 85, 100];

function DocumentRow({
  doc,
  onDelete,
  onDownload,
}: {
  doc: DocumentTranslation;
  onDelete: () => void;
  onDownload: (type: "original" | "translated") => void;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [displayProgress, setDisplayProgress] = useState(Math.max(doc.progress, 5));

  // Sync up when the real value from polling advances
  useEffect(() => {
    setDisplayProgress((prev) => Math.max(prev, doc.progress || 5));
  }, [doc.progress]);

  // Slowly interpolate toward the next checkpoint between polls
  useEffect(() => {
    if (doc.status !== "processing") return;
    const nextCheckpoint = PROGRESS_CHECKPOINTS.find((c) => c > doc.progress) ?? 100;
    const cap = nextCheckpoint - 2;
    const interval = setInterval(() => {
      setDisplayProgress((prev) => (prev >= cap ? prev : Math.min(prev + 0.2, cap)));
    }, 400);
    return () => clearInterval(interval);
  }, [doc.progress, doc.status]);

  return (
    <div className={classNames.DocumentRow}>
      <div className={classNames.DocumentIcon}>
        <IoDocumentTextOutline />
      </div>
      <div className={classNames.DocumentInfo}>
        <div className={classNames.DocumentName}>{doc.original_filename}</div>
        <div className={classNames.DocumentMeta}>
          {doc.source_language ? (
            <>
              <span>
                {getLanguageFlag(doc.source_language)}{" "}
                {getLanguageName(doc.source_language)}
              </span>
              <span className={classNames.Arrow}>→</span>
            </>
          ) : null}
          <span>
            {getLanguageFlag(doc.target_language)}{" "}
            {getLanguageName(doc.target_language)}
          </span>
          <span className={classNames.Dot}>·</span>
          <span>{new Date(doc.created_at).toLocaleDateString()}</span>
        </div>
        {doc.status === "processing" && (
          <div className={classNames.ProgressBarWrapper}>
            <div
              className={classNames.ProgressBar}
              style={{ width: `${displayProgress}%` }}
            />
          </div>
        )}
        {doc.status === "failed" && doc.error_message && (
          <div className={classNames.ErrorMessage}>{doc.error_message}</div>
        )}
      </div>
      <div className={classNames.DocumentActions}>
        {doc.status !== "processing" && <StatusBadge status={doc.status} />}
        <button
          className={classNames.IconButton}
          title="Download original"
          onClick={() => onDownload("original")}
        >
          <IoCloudDownloadOutline />
        </button>
        {doc.status === "completed" && (
          <button
            className={classNames.IconButton}
            title="Download translated"
            onClick={() => onDownload("translated")}
          >
            <IoCloudDownloadOutline />
            <span className={classNames.TranslatedLabel}>Translated</span>
          </button>
        )}
        <ConfirmActionDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          trigger={
            <button
              className={classNames.IconButton}
              onClick={() => setConfirmOpen(true)}
            >
              <IoClose />
            </button>
          }
          title="Delete document"
          action="Delete"
          content={
            <p>
              Are you sure you want to delete{" "}
              <strong>{doc.original_filename}</strong>? This will remove both
              the original and translated files.
            </p>
          }
          onConfirm={() => {
            onDelete();
            setConfirmOpen(false);
          }}
        />
      </div>
    </div>
  );
}

export default function DocumentTranslationSetting() {
  const axiosInstance = useAxios();
  const getUploadUrl = api.useGetDocumentTranslationUploadUrl(axiosInstance);
  const processDocument = api.useProcessDocumentTranslation(axiosInstance);
  const getList = api.useGetDocumentTranslationList(axiosInstance);
  const getDownloadUrl = api.useGetDocumentTranslationDownloadUrl(axiosInstance);
  const deleteDocument = api.useDeleteDocumentTranslation(axiosInstance);

  const [documents, setDocuments] = useState<DocumentTranslation[]>();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await getList();
        setDocuments(res.data.documents);
      } finally {
        setLoading(false);
      }
    })();
  }, [getList]);

  // Poll for processing documents every 5s
  useEffect(() => {
    if (!documents) return;
    const hasProcessing = documents.some((d) => d.status === "processing");
    if (!hasProcessing) return;

    const interval = setInterval(async () => {
      try {
        const res = await getList();
        setDocuments(res.data.documents);
      } catch {
        // ignore poll errors
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [documents, getList]);

  const handleFileSelect = async (file: File) => {
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast(<div className="Toast ErrorToast">{"Only PDF files are supported."}</div>);
      return;
    }

    setUploading(true);
    let createdDocId: string | undefined;
    try {
      // 1. Get presigned upload URL (creates DB record with status "processing")
      const { data } = await getUploadUrl({
        filename: file.name,
        targetLanguage: selectedLanguage,
      });
      createdDocId = data.document.id;

      // 2. Upload directly to S3
      // In stub mode s3Axios is null — use the stubbed axiosInstance instead.
      // In production use a clean instance with no auth headers for S3.
      await (s3Axios ?? axiosInstance).put(data.uploadUrl, file, {
        headers: { "Content-Type": "application/pdf" },
      });

      // 3. Trigger processing
      const { data: processData } = await processDocument(data.document.id);
      createdDocId = undefined; // success — no cleanup needed
      setDocuments((prev) => [processData.document, ...(prev ?? [])]);
    } catch (err) {
      console.error("Upload failed:", err);
      if (createdDocId) {
        deleteDocument(createdDocId).catch(() => { });
      }
      const message =
        axios.isAxiosError(err)
          ? (err.response?.data?.message ?? "Upload failed. Please try again.")
          : "Upload failed. Please try again.";
      toast(<div className="Toast ErrorToast">{message}</div>);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownload = async (
    docId: string,
    type: "original" | "translated"
  ) => {
    try {
      const { data } = await getDownloadUrl(docId, type);
      window.open(data.url, "_blank");
    } catch {
      alert("Download link unavailable.");
    }
  };

  const handleDelete = async (docId: string) => {
    try {
      await deleteDocument(docId);
      setDocuments((prev) => prev?.filter((d) => d.id !== docId));
    } catch {
      alert("Failed to delete document.");
    }
  };

  if (loading) return <Loader />;

  return (
    <Setting
      title="Document translation"
      subtitle="Upload a PDF to translate it into your desired language. Both the original and translated files are stored for your organization."
    >
      <div className={classNames.DocumentTranslation}>
        {/* Upload section */}
        <div className={classNames.UploadSection}>
          <div className={classNames.UploadControls}>
            <Button
              backgroundColor="primary"
              disabled={uploading || !selectedLanguage}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? "Uploading..." : "Upload PDF"}
            </Button>
            <div className={classNames.TranslateTo}>
              <span className={classNames.TranslateToArrow}>→</span>
              <span className={classNames.TranslateToLabel}>Translate to</span>
            </div>
            <LanguageSelect
              selectedLanguage={selectedLanguage}
              onChange={setSelectedLanguage}
              languages={ALL_LANGUAGES}
              trigger={
                <Button>
                  <span>{selectedLanguage ? getLanguageName(selectedLanguage) : "Choose language"}</span>
                  <IoChevronDown />
                </Button>
              }
            />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            className={classNames.HiddenInput}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
            }}
          />
          <p className={classNames.UploadHint}>
            Upload a PDF and select the output language.
          </p>
        </div>

        {/* Documents list */}
        {documents && documents.length > 0 ? (
          <div className={classNames.DocumentList}>
            {documents.map((doc) => (
              <DocumentRow
                key={doc.id}
                doc={doc}
                onDelete={() => handleDelete(doc.id)}
                onDownload={(type) => handleDownload(doc.id, type)}
              />
            ))}
          </div>
        ) : (
          <div className={classNames.EmptyState}>
            <IoDocumentTextOutline />
            <p>No documents uploaded yet.</p>
          </div>
        )}
      </div>
    </Setting>
  );
}
